import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";

let msalInstance = null;
let statusCheckInterval = null;
let refreshInProgress = false;

/**
 * Initialize the TokenService with MSAL instance
 * @param {object} instance - MSAL instance
 */
export const initializeTokenService = (instance) => {
  msalInstance = instance;

  // Start periodic status check
  startStatusCheck();

  // Ensure session start time is set
  ensureSessionStartTime();
};

/**
 * Ensure session start time is set
 */
export const ensureSessionStartTime = () => {
  if (!localStorage.getItem("sessionStartTime")) {
    const currentTime = Date.now();
    localStorage.setItem("sessionStartTime", currentTime.toString());
  }
};

/**
 * Start periodic status check
 */
const startStatusCheck = () => {
  // Clear any existing interval
  if (statusCheckInterval) {
    clearInterval(statusCheckInterval);
  }

  // Check status every minute
  statusCheckInterval = setInterval(async () => {
    try {
      // Check if token is expired or about to expire
      if (isTokenExpired()) {
        const accountId = localStorage.getItem("accountId");
        if (accountId && !refreshInProgress) {
          console.log(
            "Token expired or about to expire. Attempting refresh..."
          );
          await refreshToken(accountId);
        }
      }
    } catch (error) {
      console.error("Error during token check:", error);
    }
  }, 60000); // 60000 ms = 1 minute

  // Also check immediately
  isTokenExpired();
};

/**
 * Stop periodic status check
 */
export const stopStatusCheck = () => {
  if (statusCheckInterval) {
    clearInterval(statusCheckInterval);
    statusCheckInterval = null;
  }
};

/**
 * Formats time difference in a human-readable format
 * @param {number} milliseconds - Time difference in milliseconds
 * @returns {string} - Formatted time string
 */
const formatTimeDifference = (milliseconds) => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours} hours ${minutes % 60} minutes`;
  } else if (minutes > 0) {
    return `${minutes} minutes ${seconds % 60} seconds`;
  } else {
    return `${seconds} seconds`;
  }
};

/**
 * Acquires a new access token using the refresh token
 * @param {string} accountId - The account ID to refresh the token for
 * @returns {Promise<string>} - The new access token
 */
export const refreshToken = async (accountId) => {
  if (!msalInstance) {
    throw new Error("TokenService not initialized with MSAL instance");
  }

  if (refreshInProgress) {
    console.log("Token refresh already in progress");
    return;
  }

  refreshInProgress = true;

  try {
    console.log("Starting token refresh...");
    const account = msalInstance.getAccountByHomeId(accountId);

    if (!account) {
      throw new Error("No account found");
    }

    // Use the same scopes as login request
    const silentRequest = {
      ...loginRequest,
      account: account,
    };

    const response = await msalInstance.acquireTokenSilent(silentRequest);

    // Update storage with new token info
    localStorage.setItem("accessToken", response.accessToken);
    localStorage.setItem("tokenExpiryTime", response.expiresOn.getTime());
    localStorage.setItem("microsoft_code", response.accessToken);

    console.log("Token refreshed successfully");
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      console.log("Interactive login required, redirecting...");
      // User needs to reauthenticate
      msalInstance.loginRedirect(loginRequest);
    } else {
      console.error("Token refresh failed:", error);
      throw error;
    }
  } finally {
    refreshInProgress = false;
  }
};

/**
 * Checks if the current token is expired or about to expire
 * @returns {boolean} - True if token is expired or about to expire
 */
export const isTokenExpired = () => {
  const tokenExpiryTime = localStorage.getItem("tokenExpiryTime");

  if (!tokenExpiryTime) {
    return true;
  }

  const expiryTime = parseInt(tokenExpiryTime);
  const currentTime = Date.now();
  const timeUntilExpiry = expiryTime - currentTime;
  const fiveMinutesInMs = 300000;

  // Log session insights
  const sessionStartTime = localStorage.getItem("sessionStartTime");

  if (sessionStartTime) {
    const sessionDuration = currentTime - parseInt(sessionStartTime);
    const sessionStartDate = new Date(parseInt(sessionStartTime));
    const expiryDate = new Date(expiryTime);
    const renewalDate = new Date(expiryTime - fiveMinutesInMs);

    // Calculate next check time
    const nextCheckTime = new Date(Date.now() + 60000);

    // Format dates in the requested format
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = date
        .toLocaleString("en-US", { month: "short" })
        .toUpperCase();
      const day = date.getDate();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12;

      return `${year} ${month} ${day}__${formattedHours}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")} ${ampm}`;
    };

    // Format time duration as hh:mm:ss
    const formatDuration = (milliseconds) => {
      const totalSeconds = Math.floor(milliseconds / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    // Calculate total session validity time (from login to expiry)
    const totalSessionValidity = expiryTime - parseInt(sessionStartTime);

    // Combined log message with improved readability
    console.log(`Session Status:
    Total session duration: ${formatDuration(totalSessionValidity)}
    Login time: ${formatDate(sessionStartDate)}
    Active duration: ${formatDuration(sessionDuration)}
    Expiry time: ${formatDate(expiryDate)}
    Time until expiry: ${formatDuration(timeUntilExpiry)}
    Token refresh time: ${formatDate(renewalDate)}
    Refresh in: ${formatTimeDifference(timeUntilExpiry - fiveMinutesInMs)}`);
  } else {
    // Try to set session start time if missing
    ensureSessionStartTime();
  }

  // Check 5 minutes before expiry (300000 ms)
  const isExpired = currentTime >= expiryTime - fiveMinutesInMs;

  return isExpired;
};

/**
 * Sets the token expiry time in localStorage
 * @param {number} expiresIn - Time in seconds until token expires
 */
export const setTokenExpiryTime = (expiresIn) => {
  const expiryTime = Date.now() + expiresIn * 1000;
  localStorage.setItem("tokenExpiryTime", expiryTime.toString());

  // Set session start time if not already set
  if (!localStorage.getItem("sessionStartTime")) {
    localStorage.setItem("sessionStartTime", Date.now().toString());
  }
};

// Export all functions as a single object for backward compatibility
const TokenService = {
  initializeTokenService,
  refreshToken,
  isTokenExpired,
  setTokenExpiryTime,
  stopStatusCheck,
  ensureSessionStartTime,
};

export default TokenService;
